// =============================================================================
// iTravel 2.0 — Notifications API Hooks
// =============================================================================
// TanStack Query hooks for all notification-related API calls:
//   - List notifications (paginated, filtered)
//   - Get unread count (with type breakdown)
//   - Mark as read (single, bulk, all)
//   - Delete notifications (single, all)
//
// All hooks use the configured ky API instance from @/lib/api/instance.
// =============================================================================

import { api } from '@/lib/api/instance';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// =============================================================================
// TYPES
// =============================================================================

export type NotificationType =
  | 'FRIEND_REQUEST'
  | 'FRIEND_ACCEPTED'
  | 'TRIP_INVITE'
  | 'TRIP_UPDATE'
  | 'EXPENSE_ADDED'
  | 'EXPENSE_SETTLED'
  | 'PHOTO_ADDED'
  | 'FEED_REACTION'
  | 'FEED_COMMENT'
  | 'CHECKLIST_ASSIGNED'
  | 'TRIP_REMINDER'
  | 'AI_GUIDE_READY'
  | 'AI_SUMMARY_READY'
  | 'SYSTEM'
  | 'FOLLOW'
  | 'FOLLOW_REQUEST'
  | 'FOLLOW_ACCEPTED';

export interface NotificationResponse {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  /**
   * TRIP_INVITE only. The notification records that you were asked; this says
   * whether you have answered. Derived server-side from the invitation and the
   * membership, so it survives a reload — which is the whole point, since the
   * buttons used to come back on every visit to the screen.
   *
   * Null for other types, and for an invite to a trip that has been deleted.
   */
  invitationState?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | null;
}

export interface UnreadCountResponse {
  unreadCount: number;
  byType?: Record<string, number>;
}

export interface NotificationMessageResponse {
  message: string;
  count?: number;
}

export interface PaginationMeta {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params?: NotificationsListParams) => [...notificationKeys.all, 'list', params] as const,
  detail: (notificationId: string) => [...notificationKeys.all, 'detail', notificationId] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// =============================================================================
// LIST NOTIFICATIONS (paginated, filtered)
// =============================================================================

export interface NotificationsListParams {
  page?: number;
  perPage?: number;
  isRead?: boolean;
  type?: NotificationType;
}

export function useNotificationsList(params?: NotificationsListParams) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async (): Promise<{
      data: NotificationResponse[];
      meta: PaginationMeta;
    }> => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.perPage) searchParams.set('perPage', String(params.perPage));
      if (params?.isRead !== undefined) searchParams.set('isRead', String(params.isRead));
      if (params?.type) searchParams.set('type', params.type);

      const query = searchParams.toString();
      const url = query ? `notifications?${query}` : 'notifications';
      return api.get(url).json();
    },
    staleTime: 15_000, // 15 seconds — notifications should be relatively fresh
  });
}

// =============================================================================
// GET SINGLE NOTIFICATION
// =============================================================================

export function useNotificationDetail(notificationId: string) {
  return useQuery({
    queryKey: notificationKeys.detail(notificationId),
    queryFn: async (): Promise<{ data: NotificationResponse }> => {
      return api.get(`notifications/${notificationId}`).json();
    },
    enabled: !!notificationId,
    staleTime: 30_000,
  });
}

// =============================================================================
// GET UNREAD COUNT
// =============================================================================

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async (): Promise<{ data: UnreadCountResponse }> => {
      return api.get('notifications/unread-count').json();
    },
    staleTime: 15_000, // 15 seconds
    refetchInterval: 30_000, // Poll every 30 seconds for real-time badge updates
    refetchIntervalInBackground: true, // Keep polling even when tab is not focused
  });
}

// =============================================================================
// MARK SINGLE NOTIFICATION AS READ
// =============================================================================

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string): Promise<{ data: NotificationResponse }> => {
      return api.patch(`notifications/${notificationId}/read`).json();
    },
    onSuccess: (_data, notificationId) => {
      // Update the specific notification in cache if it exists
      queryClient.invalidateQueries({
        queryKey: notificationKeys.detail(notificationId),
      });
      // Invalidate the list so read status updates
      queryClient.invalidateQueries({
        queryKey: [...notificationKeys.all, 'list'],
      });
      // Refresh the unread count badge
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

// =============================================================================
// MARK MULTIPLE NOTIFICATIONS AS READ (bulk)
// =============================================================================

export function useMarkManyNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      notificationIds: string[],
    ): Promise<{ data: NotificationMessageResponse }> => {
      return api
        .post('notifications/read', {
          json: { notificationIds },
        })
        .json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...notificationKeys.all, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

// =============================================================================
// MARK ALL NOTIFICATIONS AS READ
// =============================================================================

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ data: NotificationMessageResponse }> => {
      return api.post('notifications/read-all').json();
    },
    onSuccess: () => {
      // Invalidate all notification queries so everything refreshes
      queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
}

// =============================================================================
// DELETE SINGLE NOTIFICATION
// =============================================================================

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string): Promise<{ data: NotificationMessageResponse }> => {
      return api.delete(`notifications/${notificationId}`).json();
    },
    onSuccess: (_data, notificationId) => {
      // Remove the specific notification from cache
      queryClient.removeQueries({
        queryKey: notificationKeys.detail(notificationId),
      });
      // Refresh the list and unread count
      queryClient.invalidateQueries({
        queryKey: [...notificationKeys.all, 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

// =============================================================================
// DELETE ALL NOTIFICATIONS
// =============================================================================

export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ data: NotificationMessageResponse }> => {
      return api.delete('notifications').json();
    },
    onSuccess: () => {
      // Clear all notification-related queries from cache
      queryClient.invalidateQueries({
        queryKey: notificationKeys.all,
      });
    },
  });
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Returns the icon name / emoji associated with a notification type.
 * Useful for rendering notification icons in the UI.
 */
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'FRIEND_REQUEST':
      return 'person_add';
    case 'FRIEND_ACCEPTED':
      return 'how_to_reg';
    case 'FOLLOW':
    case 'FOLLOW_ACCEPTED':
      return 'person_add';
    case 'FOLLOW_REQUEST':
      return 'person_add_alt';
    case 'TRIP_INVITE':
      return 'mail';
    case 'TRIP_UPDATE':
      return 'update';
    case 'EXPENSE_ADDED':
      return 'receipt_long';
    case 'EXPENSE_SETTLED':
      return 'payments';
    case 'PHOTO_ADDED':
      return 'photo_camera';
    case 'FEED_REACTION':
      return 'favorite';
    case 'FEED_COMMENT':
      return 'comment';
    case 'CHECKLIST_ASSIGNED':
      return 'assignment_ind';
    case 'TRIP_REMINDER':
      return 'alarm';
    case 'AI_GUIDE_READY':
      return 'auto_awesome';
    case 'AI_SUMMARY_READY':
      return 'summarize';
    case 'SYSTEM':
    default:
      return 'notifications';
  }
}

/**
 * Returns a color string associated with a notification type.
 * Useful for color-coding notifications in the UI.
 */
export function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'FRIEND_REQUEST':
    case 'FRIEND_ACCEPTED':
    case 'FOLLOW':
    case 'FOLLOW_REQUEST':
    case 'FOLLOW_ACCEPTED':
      return '#2196f3'; // blue
    case 'TRIP_INVITE':
    case 'TRIP_UPDATE':
    case 'TRIP_REMINDER':
      return '#4caf50'; // green
    case 'EXPENSE_ADDED':
    case 'EXPENSE_SETTLED':
      return '#ff9800'; // orange
    case 'PHOTO_ADDED':
      return '#9c27b0'; // purple
    case 'FEED_REACTION':
      return '#e91e63'; // pink
    case 'FEED_COMMENT':
      return '#00bcd4'; // cyan
    case 'CHECKLIST_ASSIGNED':
      return '#795548'; // brown
    case 'AI_GUIDE_READY':
    case 'AI_SUMMARY_READY':
      return '#673ab7'; // deep purple
    case 'SYSTEM':
    default:
      return '#607d8b'; // blue-grey
  }
}

/**
 * Where a notification of this type and payload should take you.
 *
 * Split out from {@link getNotificationLink} because push notifications carry
 * the same information in a different shape — FCM flattens every value to a
 * string — and two copies of this map would drift the moment one gained a case.
 * Values are read loosely for that reason: a string is all either source
 * guarantees.
 */
export function linkForNotification(
  type: string,
  data: Record<string, unknown> | null | undefined,
): string | null {
  if (!data) return null;

  const id = (key: string): string | null => {
    const value = data[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
  };

  const tripId = id('tripId');

  switch (type) {
    // Legacy friendship rows: the friends screen is gone, the profile holds follows now.
    case 'FRIEND_REQUEST':
    case 'FRIEND_ACCEPTED':
      return '/profile';
    case 'FOLLOW':
    case 'FOLLOW_ACCEPTED': {
      const userId = id('userId');
      return userId ? `/profile/${userId}` : '/profile';
    }
    case 'FOLLOW_REQUEST':
      return '/profile';

    // TRIP_UPDATE is only ever sent for a new post, and a post lives in the
    // conversation — not on the trip's front page, which was where every "X
    // posted in your trip" tap used to land. The postId is what says so; a
    // TRIP_UPDATE without one is either older than this or genuinely about the
    // trip, and the trip page is the right answer for both.
    case 'TRIP_UPDATE':
      if (tripId && id('postId')) return `/trips/${tripId}/feed`;
      return tripId ? `/trips/${tripId}` : '/trips';

    case 'TRIP_INVITE':
    case 'TRIP_REMINDER':
      return tripId ? `/trips/${tripId}` : '/trips';

    case 'EXPENSE_ADDED':
    case 'EXPENSE_SETTLED': {
      const expenseId = id('expenseId');
      if (tripId && expenseId) return `/trips/${tripId}/expenses/${expenseId}`;
      return tripId ? `/trips/${tripId}/expenses` : null;
    }

    case 'PHOTO_ADDED':
      return tripId ? `/trips/${tripId}/photos` : null;

    // Posts are only reachable through their trip — there is no standalone
    // /feed/:postId route, and pointing at one sent every reaction and comment
    // to the 404 page.
    case 'FEED_REACTION':
    case 'FEED_COMMENT':
      return tripId ? `/trips/${tripId}/feed` : null;

    case 'CHECKLIST_ASSIGNED':
      return tripId ? `/trips/${tripId}/checklist` : null;

    case 'AI_GUIDE_READY': {
      const dayId = id('dayId');
      const pointId = id('pointId');
      if (tripId && dayId && pointId) return `/trips/${tripId}/plan/${dayId}/${pointId}`;
      return null;
    }

    case 'AI_SUMMARY_READY':
      return tripId ? `/trips/${tripId}/summary` : null;

    case 'SYSTEM':
    default:
      return null;
  }
}

/**
 * Build a navigation path from notification data for deep-linking.
 * Returns the route the user should be navigated to when tapping the notification.
 */
export function getNotificationLink(notification: NotificationResponse): string | null {
  return linkForNotification(notification.type, notification.data);
}
